import { getFirebaseAuth } from './firebase-init';

// Defaults to same-origin /api since the app is now always served alongside its own
// backend (nginx proxies /api/ to the Node server) — no more "Server URL" login field.
function getApiUrl() {
  return localStorage.getItem('mt_api_url') || `${window.location.origin}/api`;
}

async function request(path, options = {}) {
  const url = `${getApiUrl()}${path}`;
  const currentUser = getFirebaseAuth().currentUser;
  const token = currentUser ? await currentUser.getIdToken() : null;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  // CSV responses
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('text/csv')) return res.blob();
  return res.json();
}

export const api = {
  // Server URL — which backend to talk to, orthogonal to who's signed in
  setApiUrl(apiUrl) {
    localStorage.setItem('mt_api_url', apiUrl);
  },
  getApiUrl,

  // Users
  getMe: () => request('/users/me'),
  getUsers: () => request('/users'),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),

  // Medications
  getMedications: (userId) => request(`/medications${userId ? `?userId=${userId}` : ''}`),
  createMedication: (data) => request('/medications', { method: 'POST', body: JSON.stringify(data) }),
  updateMedication: (id, data) => request(`/medications/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteMedication: (id) => request(`/medications/${id}`, { method: 'DELETE' }),
  getOverdueMedications: () => request('/medications/overdue'),

  // Logs
  logDose: (data) => request('/logs', { method: 'POST', body: JSON.stringify(data) }),
  getLogs: (userId, from, to) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return request(`/logs?${params}`);
  },
  getCalendar: (userId, year, month) => {
    const params = new URLSearchParams({ year, month });
    if (userId) params.set('userId', userId);
    return request(`/logs/calendar?${params}`);
  },
  getDay: (userId, date) => {
    const params = new URLSearchParams({ date });
    if (userId) params.set('userId', userId);
    return request(`/logs/day?${params}`);
  },
  getStreak: (medicationId) => request(`/logs/streak/${medicationId}`),
  exportCSV: async (userId, userName) => {
    const params = userId ? `?userId=${userId}` : '';
    const blob = await request(`/logs/export${params}`, {
      headers: { 'Content-Type': '' },
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${userName || 'meds'}-history.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
